import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import slugify from '@sindresorhus/slugify';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import CheckIcon from 'assets/icons/check_icon.svg?react';
import ChevronIcon from 'assets/icons/chevron_right.svg?react';
import HiddenLink from 'components/HiddenLink';
import formatDirectionName from 'helpers/formatDirectionName';

const Card = styled('div')(() => ({
  backgroundColor: 'rgba(250, 250, 250, 1)',
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: 18,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.9
  }
}));

const LinkStyled = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#fff',
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 8,
  padding: 10,
  marginLeft: -4,
  marginRight: -4,
  marginBottom: 8,
  cursor: 'pointer'
}));

const LinkTitleStyled = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 16,
  lineHeight: '24px',
  color: 'rgba(10, 10, 10, 1)'
}));

const CardRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: 8
}));

const CardValue = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 400,
  color: 'rgba(10, 10, 10, 1)'
}));

const Flex = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16
}));

const Logo = styled('img')(() => ({
  height: 32,
  maxWidth: 120,
  objectFit: 'contain'
}));

const PlatformCard = ({ item, title, items = [], id, slug, tDirections }) => {
  const navigate = useNavigate();

  const displayTitle = item?.name || item?.title || title || '';
  const platformId = item?._id || id;
  const platformSlug = slug || slugify(displayTitle || 'platform');
  const logo = item?.logo;
  const directions = item?.directions || [];
  const listItems = items?.length ? items : directions;

  const handleNavigate = React.useCallback(() => {
    if (!platformId) return;
    navigate(`/education/platforms/${platformSlug}/${platformId}`);
  }, [navigate, platformId, platformSlug]);

  const href = React.useMemo(
    () => (platformId ? `/education/platforms/${platformSlug}/${platformId}` : '#'),
    [platformId, platformSlug]
  );

  return (
    <Card onClick={handleNavigate}>
      <HiddenLink href={href} />
      <LinkStyled>
        <Flex>
          {logo && <Logo src={`${logo}/public`} alt={displayTitle} />}
          <LinkTitleStyled>{displayTitle}</LinkTitleStyled>
        </Flex>
        <ChevronIcon />
      </LinkStyled>

      {React.useMemo(
        () =>
          (listItems || []).slice(0, 5).map((value, i) => (
            <CardRow key={`${value}-${i}`}>
              <CheckIcon />
              <CardValue>{formatDirectionName(value, tDirections)}</CardValue>
            </CardRow>
          )),
        [listItems, tDirections]
      )}
    </Card>
  );
};

export default React.memo(PlatformCard);
